// Collapsible department-grouped sidebar.
//
// Expanded: 240px. Home + pinned tools + department groups (each toggleable).
// Collapsed: 60px icon rail. Home + pinned tool icons + department icons.
//
// Active state is driven by the URL via useLocation, matching the pathname
// against each tool's real route (the same mapping used for navigation).
// Default-open departments come from data, then any department that contains
// the current route's tool is also force-opened on mount.

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, ChevronDown, Pin } from 'lucide-react';
import {
  COCKPIT_DEPARTMENTS,
  DEFAULT_OPEN_DEPTS,
  PINNED_TOOLS,
  findTool,
  isLaunched,
  type Department,
  type Tool,
} from '@/data/tools.config';
import { DeptIcon } from '@/components/DeptIcon';
import { usePins } from '@/lib/usePins';
import { useSession } from '@/lib/useSession';
import { isDeptVisible, isToolPermitted, visibleToolsFor } from '@/lib/toolVisibility';
import { toolHrefFor, activeToolForPath } from '@/lib/toolRouting';
import { useHqBadges, type HqBadges } from '@/lib/useStores';
import { formatBadgeCount, formatMiniBadgeCount } from '@/lib/badges';

// Which sidebar links carry an alert-bubble count, and which field of
// GET /v1/stores/hq/badges feeds each one. One shared poll (useHqBadges,
// called once in <Sidebar>) backs every bubble; a tool not listed here never
// renders one. The accessible label per tool ("3 open" / "3 today") is kept
// alongside so a screen reader gets a real phrase, not a bare number.
const TOOL_BADGE_FIELD: Record<string, keyof HqBadges> = {
  'stores-hq-questions': 'questions_open',
  'stores-hq-pulse': 'pulse_open',
  'stores-hq-photos': 'photos_today',
};
const TOOL_BADGE_ARIA_SUFFIX: Record<string, string> = {
  'stores-hq-questions': 'open',
  'stores-hq-pulse': 'open',
  'stores-hq-photos': 'today',
};

function badgeCountFor(toolId: string, badges: HqBadges | null): number {
  if (!badges) return 0;
  const field = TOOL_BADGE_FIELD[toolId];
  return field ? badges[field] ?? 0 : 0;
}

// Sum of every badge-bearing, currently-visible tool in a department. Feeds
// the single mini dot on that department's icon in the collapsed rail, where
// there is no per-tool row to hang a bubble on.
function deptBadgeTotal(
  dept: Department,
  perms: string[] | undefined,
  superAdmin: boolean,
  badges: HqBadges | null,
): number {
  if (!badges) return 0;
  let total = 0;
  for (const t of visibleToolsFor(dept, perms, superAdmin)) {
    total += badgeCountFor(t.id, badges);
  }
  return total;
}

// Full-size bubble: right-aligned on an expanded/mobile nav row. Renders
// nothing at zero, per the "never render a zero" rule. The visible text caps
// at 99+; the aria-label always carries the real count.
function CountBubble({ toolId, badges }: { toolId: string; badges: HqBadges | null }) {
  const count = badgeCountFor(toolId, badges);
  if (count <= 0) return null;
  const suffix = TOOL_BADGE_ARIA_SUFFIX[toolId] ?? 'open';
  return (
    <span
      aria-label={`${count} ${suffix}`}
      className="ml-2 inline-flex h-5 min-w-[20px] flex-shrink-0 items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold leading-none text-white dark:bg-red-500"
    >
      <span aria-hidden="true">{formatBadgeCount(count)}</span>
    </span>
  );
}

// Mini dot for a department icon in the 60px collapsed rail: a tiny "9+"-
// capped count rather than a full bubble, so it never overflows the icon.
function MiniCountDot({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      aria-hidden="true"
      className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-0.5 text-[9px] font-bold leading-none text-white dark:bg-red-500"
    >
      {formatMiniBadgeCount(count)}
    </span>
  );
}

// Tool + department visibility is driven off the user's real backend
// permissions (see ui/src/lib/toolVisibility.ts). A tool shows only when it is
// launched AND permitted (super-admin, an exact accessSlug match, an explicit
// Everyone capability, or genuinely public). A department shows only when it
// has at least one visible tool, so a section with no reachable tools is
// hidden entirely.
const visibleTools = visibleToolsFor;

// Open-state key for the collapsible Pinned section. Lives in the same
// openDept record as the department ids (no dept uses this id).
const PINNED_SECTION_KEY = '__pinned';

// Walk the catalog to find the (dept, tool) pair for a tool slug.
function findToolBySlug(slug: string): { dept: Department; tool: Tool } | undefined {
  for (const dept of COCKPIT_DEPARTMENTS) {
    const tool = dept.tools.find((t) => t.id === slug);
    if (tool) return { dept, tool };
  }
  return undefined;
}

// What the Pinned section should render: either the user's saved pins (when
// they have ever interacted with the pin UI) or the global PINNED_TOOLS
// defaults (when they have not). Every entry is filtered through the SAME
// access model the departments below use (isToolPermitted), so a pinned tool
// the user cannot open is silently omitted rather than rendered into a link
// that lands on a gated page. This is what keeps the default 'Orders' pin
// from showing to a no-access user.
type PinnedSectionEntry = { deptId: string; toolId: string; label: string };

function resolvePinnedEntries(
  initialized: boolean,
  pins: { tool_slug: string }[],
  perms: string[] | undefined,
  superAdmin: boolean,
): PinnedSectionEntry[] {
  if (!initialized) {
    return PINNED_TOOLS.flatMap((p) => {
      const found = findTool(p.deptId, p.toolId);
      if (!found) return [];
      if (!isLaunched(found.tool)) return [];
      if (!isToolPermitted(found.tool, perms, superAdmin)) return [];
      return [{ deptId: p.deptId, toolId: p.toolId, label: p.label }];
    });
  }
  const entries: PinnedSectionEntry[] = [];
  for (const p of pins) {
    const found = findToolBySlug(p.tool_slug);
    if (!found) continue;
    if (!isLaunched(found.tool)) continue;
    if (!isToolPermitted(found.tool, perms, superAdmin)) continue;
    entries.push({ deptId: found.dept.id, toolId: found.tool.id, label: found.tool.name });
  }
  return entries;
}

type Props = {
  collapsed: boolean;
  isMobile: boolean;
  mobileOpen: boolean;
  onExpand: () => void;
};

function useActive(): { deptId: string | null; toolId: string | null; isHome: boolean } {
  const location = useLocation();
  const isHome = location.pathname === '/';
  // Resolve the active tool by matching the pathname against each catalog
  // tool's real route (the same mapping navigation uses). This works for
  // explicit routes (e.g. /compliance/coas, which carries no path params) and
  // for the /:dept/:tool catch-all alike, where useParams() previously failed
  // for the explicit ones. deptId here is the active tool's department, used
  // only to keep that section expanded, NOT to style the header active.
  const active = activeToolForPath(location.pathname);
  return { deptId: active?.deptId ?? null, toolId: active?.toolId ?? null, isHome };
}

export function Sidebar({ collapsed, isMobile, mobileOpen, onExpand }: Props) {
  const { deptId: activeDept, toolId: activeTool, isHome } = useActive();
  const navigate = useNavigate();
  const { user } = useSession();
  const perms = user?.permissions;
  const superAdmin = !!user?.superAdmin;
  const visibleDepts = useMemo(
    () => COCKPIT_DEPARTMENTS.filter((d) => isDeptVisible(d, perms, superAdmin)),
    [perms, superAdmin],
  );
  const { pins, initialized } = usePins();
  const pinnedEntries = useMemo(
    () => resolvePinnedEntries(initialized, pins, perms, superAdmin),
    [initialized, pins, perms, superAdmin],
  );
  // One shared poll for the whole sidebar; every bubble below reads off this
  // same result rather than each link fetching its own count.
  const badges = useHqBadges(perms, superAdmin);

  const initialOpen = useMemo<Record<string, boolean>>(() => {
    const acc: Record<string, boolean> = {};
    // Pinned section defaults open, alongside the default-open departments.
    acc[PINNED_SECTION_KEY] = true;
    for (const id of DEFAULT_OPEN_DEPTS) acc[id] = true;
    if (activeDept) acc[activeDept] = true;
    return acc;
  }, [activeDept]);

  const [openDept, setOpenDept] = useState<Record<string, boolean>>(initialOpen);

  // If the route changes to a new department, ensure its section is open.
  useEffect(() => {
    if (!activeDept) return;
    setOpenDept((prev) => (prev[activeDept] ? prev : { ...prev, [activeDept]: true }));
  }, [activeDept]);

  const toggleDept = (id: string) => {
    setOpenDept((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const clickDeptIconCollapsed = (id: string) => {
    onExpand();
    setOpenDept((prev) => ({ ...prev, [id]: true }));
  };

  // Mobile drawer always shows the expanded view; collapsed only applies to
  // the desktop static sidebar.
  const showCollapsedView = !isMobile && collapsed;

  const asideClass = isMobile
    ? `fixed left-0 bottom-0 z-40 w-[260px] flex-shrink-0 overflow-y-auto overflow-x-hidden border-r border-amp-line bg-amp-cream shadow-2 transition-transform duration-200 ease-out dark:border-amp-dark-line dark:bg-amp-dark-surface ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`
    : 'hidden flex-shrink-0 overflow-y-auto overflow-x-hidden border-r border-amp-line bg-amp-cream transition-[width] duration-normal ease-out md:block dark:border-amp-dark-line dark:bg-amp-dark-surface';

  const asideStyle: React.CSSProperties = isMobile
    ? { top: 56, padding: '16px 10px' }
    : { width: collapsed ? 60 : 240, padding: '16px 10px' };

  return (
    <aside
      aria-label="Primary"
      aria-hidden={isMobile && !mobileOpen ? true : undefined}
      className={asideClass}
      style={asideStyle}
    >
      <nav aria-label="Primary navigation">
        {showCollapsedView ? (
          <CollapsedView
            departments={visibleDepts}
            perms={perms}
            superAdmin={superAdmin}
            activeDeptId={activeDept}
            isHome={isHome}
            onDeptIconClick={clickDeptIconCollapsed}
            badges={badges}
          />
        ) : (
          <ExpandedView
            departments={visibleDepts}
            perms={perms}
            superAdmin={superAdmin}
            openDept={openDept}
            toggleDept={toggleDept}
            activeDeptId={activeDept}
            activeToolId={activeTool}
            isHome={isHome}
            pinnedEntries={pinnedEntries}
            initialized={initialized}
            onPinnedClick={(dept, tool) => {
              navigate(toolHrefFor(dept.id, tool.id));
            }}
            badges={badges}
          />
        )}
      </nav>
    </aside>
  );
}

// ----- Expanded view ---------------------------------------------------------

type ExpandedProps = {
  departments: Department[];
  perms: string[] | undefined;
  superAdmin: boolean;
  openDept: Record<string, boolean>;
  toggleDept: (id: string) => void;
  activeDeptId: string | null;
  activeToolId: string | null;
  isHome: boolean;
  pinnedEntries: PinnedSectionEntry[];
  initialized: boolean;
  onPinnedClick: (dept: Department, tool: Tool) => void;
  badges: HqBadges | null;
};

function ExpandedView({
  departments,
  perms,
  superAdmin,
  openDept,
  toggleDept,
  activeDeptId,
  activeToolId,
  isHome,
  pinnedEntries,
  initialized,
  onPinnedClick,
  badges,
}: ExpandedProps) {
  const showEmptyHint = initialized && pinnedEntries.length === 0;
  const pinnedOpen = !!openDept[PINNED_SECTION_KEY];
  return (
    <>
      <Link
        to="/"
        className={navItemClass(isHome)}
        aria-current={isHome ? 'page' : undefined}
      >
        <span
          aria-hidden
          className="inline-flex items-center justify-center"
          style={{ width: 20, height: 20 }}
        >
          <Home size={15} />
        </span>
        <span>Home</span>
      </Link>

      {/* Pinned: a collapsible section with its pin icon on the LEFT and the
          same chevron behavior as the department sections. */}
      <div style={{ marginTop: 6, marginBottom: 2 }}>
        <SectionHeader
          icon={<Pin aria-hidden size={15} />}
          label="Pinned"
          open={pinnedOpen}
          controls="nav-pinned"
          onToggle={() => toggleDept(PINNED_SECTION_KEY)}
        />
        {pinnedOpen ? (
          <div id="nav-pinned">
            {showEmptyHint ? (
              <div
                className="text-amp-ink-muted dark:text-amp-dark-ink-muted"
                style={{
                  padding: '6px 12px 8px 52px',
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: 1.4,
                }}
              >
                No pins yet. Click the pin icon on any tool to add it here.
              </div>
            ) : (
              pinnedEntries.map((p) => {
                const found = findTool(p.deptId, p.toolId);
                if (!found) return null;
                const isActive =
                  activeDeptId === p.deptId && activeToolId === p.toolId;
                return (
                  <button
                    key={`${p.deptId}.${p.toolId}`}
                    type="button"
                    onClick={() => onPinnedClick(found.dept, found.tool)}
                    className={navItemNestedClass(isActive)}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span style={{ flex: 1, textAlign: 'left' }}>{p.label}</span>
                    <CountBubble toolId={p.toolId} badges={badges} />
                  </button>
                );
              })
            )}
          </div>
        ) : null}
      </div>

      {/* Departments become top-level collapsible sections, each rendered as
          a full-width banded row (section 2.9 / 2.12): the band is the only
          thing a COLLAPSED department shows, no tool count or preview text. */}
      {departments.map((d) => {
        const launchedTools = visibleTools(d, perms, superAdmin);
        if (launchedTools.length === 0) return null;
        const open = !!openDept[d.id];
        const listId = `nav-dept-${d.id}`;
        return (
          <div key={d.id} style={{ marginBottom: 2 }}>
            {/* Department headers are never styled active. Being on a tool
                highlights only that tool row; the header may stay expanded
                (via openDept) but is not given the active treatment. */}
            <SectionHeader
              icon={<DeptIcon deptId={d.id} size={15} />}
              label={d.name}
              open={open}
              controls={listId}
              onToggle={() => toggleDept(d.id)}
            />
            {open ? (
              <div id={listId}>{renderToolRows(d, launchedTools, activeDeptId, activeToolId, badges)}</div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

// Renders a department's visible tools as nested rows. When consecutive
// tools carry the same `group` (Stores only, today), a small subgroup label
// is inserted above the first tool of each new group. Tools with no group
// (every other department) render exactly as a plain nested list, unchanged.
function renderToolRows(
  dept: Department,
  tools: Tool[],
  activeDeptId: string | null,
  activeToolId: string | null,
  badges: HqBadges | null,
): React.ReactNode {
  const rows: React.ReactNode[] = [];
  let lastGroup: string | undefined;
  for (const t of tools) {
    if (t.group && t.group !== lastGroup) {
      rows.push(
        <div key={`group-${dept.id}-${t.group}`} className={subgroupLabelClass}>
          {t.group}
        </div>,
      );
    }
    lastGroup = t.group;
    const isActive = activeDeptId === dept.id && activeToolId === t.id;
    rows.push(
      <Link
        key={t.id}
        to={toolHrefFor(dept.id, t.id)}
        className={navItemNestedClass(isActive)}
        aria-current={isActive ? 'page' : undefined}
      >
        <span style={{ flex: 1, textAlign: 'left' }}>{t.name}</span>
        <CountBubble toolId={t.id} badges={badges} />
      </Link>,
    );
  }
  return rows;
}

// Collapsible section header used by both the Pinned section and each
// department section: icon on the LEFT of the label, chevron on the right that
// rotates with open state. Same chevron behavior across all sections.
function SectionHeader({
  icon,
  label,
  open,
  controls,
  onToggle,
}: {
  icon: React.ReactNode;
  label: string;
  open: boolean;
  controls: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controls}
      className={deptBandClass}
    >
      <span
        aria-hidden
        className="inline-flex items-center justify-center text-amp-ink dark:text-amp-dark-ink"
        style={{ width: 20, height: 20 }}
      >
        {icon}
      </span>
      <span className="flex-1 text-left text-[11px] font-bold uppercase tracking-caps-tight text-amp-ink dark:text-amp-dark-ink">
        {label}
      </span>
      <span
        aria-hidden
        className="inline-flex text-amp-ink-muted transition-transform dark:text-amp-dark-ink-muted"
        style={{
          transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          transitionDuration: '150ms',
        }}
      >
        <ChevronDown size={13} />
      </span>
    </button>
  );
}

// ----- Collapsed view --------------------------------------------------------

type CollapsedProps = {
  departments: Department[];
  perms: string[] | undefined;
  superAdmin: boolean;
  activeDeptId: string | null;
  isHome: boolean;
  onDeptIconClick: (id: string) => void;
  badges: HqBadges | null;
};

function CollapsedView({
  departments,
  perms,
  superAdmin,
  activeDeptId,
  isHome,
  onDeptIconClick,
  badges,
}: CollapsedProps) {
  return (
    <div className="flex flex-col" style={{ gap: 2 }}>
      <Link
        to="/"
        title="Home"
        aria-label="Home"
        aria-current={isHome ? 'page' : undefined}
        className={navIconClass(isHome)}
      >
        <Home aria-hidden size={16} />
      </Link>

      <Rule />

      {departments.filter((d) => visibleTools(d, perms, superAdmin).length > 0).map((d) => {
        const isActive = activeDeptId === d.id;
        const badgeTotal = deptBadgeTotal(d, perms, superAdmin, badges);
        return (
          <button
            key={d.id}
            type="button"
            title={d.name}
            aria-label={badgeTotal > 0 ? `Open ${d.name}, ${badgeTotal} open` : `Open ${d.name}`}
            onClick={() => onDeptIconClick(d.id)}
            className={`${navIconClass(isActive)} relative`}
          >
            <DeptIcon deptId={d.id} size={16} />
            <MiniCountDot count={badgeTotal} />
          </button>
        );
      })}
    </div>
  );
}

// ----- Atoms -----------------------------------------------------------------

function Rule() {
  return (
    <div
      aria-hidden
      className="bg-amp-line dark:bg-amp-dark-line"
      style={{ height: 1, margin: '8px 8px 6px' }}
    />
  );
}

function navItemClass(active: boolean): string {
  const base =
    'flex w-full items-center gap-2.5 rounded-md border-0 text-left no-underline whitespace-nowrap transition-colors duration-fast ease-out focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40';
  const sizing = 'py-[7px] px-2.5 text-[13.5px]';
  const idle =
    'bg-transparent text-amp-ink hover:bg-amp-cream-warm dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';
  const on =
    'bg-amp-white text-amp-ink shadow-1 dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink';
  return `${base} ${sizing} ${active ? `${on} font-medium` : `${idle} font-normal`}`
    .replace(/\s+/g, ' ')
    .trim();
}

function navItemNestedClass(active: boolean): string {
  // Nested tool items align their text under the parent band's label word,
  // one indent deeper than the pre-banded design (was pl-[40px]) so a tool
  // row can never be confused with a band: pl-[49px] padding + a 3px active
  // rail = 52px to the text, 12px more than before. The rail is a
  // border-left that is always present at 3px width but transparent when
  // idle, so toggling active never shifts the text (no layout jump).
  const base =
    'flex w-full items-center gap-2.5 rounded-r-md border-0 border-l-[3px] text-left no-underline whitespace-nowrap transition-colors duration-fast ease-out focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40';
  const sizing = 'py-[7px] pr-2.5 pl-[49px] text-[13px]';
  const idle =
    'border-transparent bg-transparent text-amp-ink hover:bg-amp-cream-warm dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';
  const on =
    'border-amp-olive bg-amp-white text-amp-ink shadow-1 dark:border-amp-olive-dark dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink';
  return `${base} ${sizing} ${active ? `${on} font-medium` : `${idle} font-normal`}`
    .replace(/\s+/g, ' ')
    .trim();
}

// Banded department/Pinned section header (Option A sidebar redesign,
// 2026-08-08, COCKPIT.md 2.12). Bleeds edge-to-edge across the aside's own
// 10px horizontal padding (-mx-2.5, then px-2.5 back on the content) so it
// reads as a continuous band rather than a rounded nav-item button. Hairline
// top and bottom in the line color; label is a small bold uppercase caption
// in ink (never olive/muted, so the band stays legible as a real heading).
const deptBandClass =
  'flex w-full items-center gap-2.5 -mx-2.5 px-2.5 py-2 border-0 border-t border-b border-amp-line bg-amp-cream-warm text-left whitespace-nowrap transition-colors duration-fast ease-out focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm';

// Subgroup label rendered above the first tool of each new `group` run
// inside a department's tool list (Stores only, today). Never clickable,
// never collapsible: purely a small caption, indented to the same 52px
// text column the tool rows below it use (pl-[49px] + the 3px rail).
// Ink rather than olive, and a step larger, so the group captions read
// clearly against the tools below them (Steven, 2026-08-08). They stay
// distinct from department bands by having no band background and sitting
// at the tool indent.
const subgroupLabelClass =
  'pl-[52px] pr-2.5 pt-2 pb-1 text-[11.5px] font-bold uppercase tracking-caps-tight text-amp-ink dark:text-amp-dark-ink';

function navIconClass(active: boolean): string {
  const base =
    'mx-auto flex items-center justify-center rounded-md border-0 transition-colors duration-fast ease-out focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40';
  const sizing = 'h-9 w-10';
  const idle =
    'bg-transparent text-amp-ink hover:bg-amp-cream-warm dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';
  const on =
    'bg-amp-white text-amp-olive-deep shadow-1 dark:bg-amp-dark-surface-warm dark:text-amp-olive-dark';
  return `${base} ${sizing} ${active ? on : idle}`
    .replace(/\s+/g, ' ')
    .trim();
}
